import { Injectable, NgZone } from '@angular/core';
import { TranslationService } from './translation.service';

export interface RecordingResult {
  audioBase64: string;
  mimeType: string;
  durationMs: number;
  interimTranscript: string;
}

@Injectable({ providedIn: 'root' })
export class SpeechToTextService {
  private mediaRecorder?: MediaRecorder;
  private stream?: MediaStream;
  private chunks: Blob[] = [];
  private startTime = 0;
  private pausedDuration = 0;
  private pausedAt = 0;

  private interimRecognition?: any;
  private interimFinal = '';
  private interimLatest = '';

  constructor(
    private zone: NgZone,
    private translation: TranslationService,
  ) {}

  isSupported(): boolean {
    return !!(navigator.mediaDevices && window.MediaRecorder);
  }

  hasBrowserSpeech(): boolean {
    return !!(
      (window as any).webkitSpeechRecognition ||
      (window as any).SpeechRecognition
    );
  }

  async start(onInterim?: (text: string) => void): Promise<void> {
    if (!this.isSupported()) {
      throw new Error('MediaRecorder is not supported in this browser');
    }
    if (this.mediaRecorder || this.stream) this.cancel();

    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.chunks = [];
    this.interimFinal = '';
    this.interimLatest = '';
    this.pausedDuration = 0;
    this.pausedAt = 0;

    const mimeType = this.pickMimeType();
    this.mediaRecorder = new MediaRecorder(
      this.stream,
      mimeType ? { mimeType } : undefined,
    );
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.mediaRecorder.start();
    this.startTime = Date.now();

    this.startInterimRecognition(onInterim);
  }

  pause(): void {
    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.pause();
      this.pausedAt = Date.now();
    }
    try {
      this.interimRecognition?.stop();
    } catch {}
  }

  resume(onInterim?: (text: string) => void): void {
    if (this.mediaRecorder?.state === 'paused') {
      this.mediaRecorder.resume();
      if (this.pausedAt) {
        this.pausedDuration += Date.now() - this.pausedAt;
        this.pausedAt = 0;
      }
    }
    this.startInterimRecognition(onInterim);
  }

  async stop(): Promise<RecordingResult> {
    return new Promise((resolve) => {
      const recorder = this.mediaRecorder;
      if (!recorder) {
        resolve({
          audioBase64: '',
          mimeType: '',
          durationMs: 0,
          interimTranscript: '',
        });
        return;
      }

      const finish = async () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(this.chunks, { type: mimeType });
        const base64 = await this.blobToBase64(blob);
        const totalDuration = Date.now() - this.startTime - this.pausedDuration;

        this.stream?.getTracks().forEach((t) => t.stop());
        this.stream = undefined;
        this.mediaRecorder = undefined;

        resolve({
          audioBase64: base64,
          mimeType,
          durationMs: Math.max(0, totalDuration),
          interimTranscript: (this.interimLatest || this.interimFinal).trim(),
        });
      };

      recorder.onstop = finish;

      try {
        this.interimRecognition?.stop();
      } catch {}

      if (recorder.state !== 'inactive') {
        recorder.stop();
      } else {
        finish();
      }
    });
  }

  cancel(): void {
    try {
      this.interimRecognition?.stop();
    } catch {}
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch {}
    }
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = undefined;
    this.mediaRecorder = undefined;
    this.chunks = [];
    this.interimFinal = '';
    this.interimLatest = '';
  }

  private pickMimeType(): string | null {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    for (const t of candidates) {
      if (MediaRecorder.isTypeSupported?.(t)) return t;
    }
    return null;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.substring(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private startInterimRecognition(onInterim?: (text: string) => void) {
    const SR: any =
      (window as any).webkitSpeechRecognition ||
      (window as any).SpeechRecognition;
    if (!SR) return;
    try {
      const r = new SR();
      r.continuous = true;
      r.interimResults = true;
      r.lang = this.recognitionLang();
      r.onresult = (event: any) => {
        let final = this.interimFinal;
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            final += result[0].transcript + ' ';
          } else {
            interim += result[0].transcript;
          }
        }
        this.interimFinal = final;
        this.interimLatest = (final + interim).trim();
        if (onInterim) {
          const text = this.interimLatest;
          this.zone.run(() => onInterim(text));
        }
      };
      r.onerror = () => {};
      r.onend = () => {};
      r.start();
      this.interimRecognition = r;
    } catch {
      // ignore — server-side Gemini will still produce the final transcript
    }
  }

  /**
   * BCP-47 tag for the Web Speech recognizer, driven by the app's *current*
   * language — NOT the browser/<html lang>. Hebrew recording failed in word
   * garden because the recognizer defaulted to en-US (the app never sets
   * <html lang>), so Hebrew speech produced no usable interim transcript.
   */
  private recognitionLang(): string {
    return this.translation.getCurrentLanguage() === 'he' ? 'he-IL' : 'en-US';
  }
}
